/** Backend POST /api/emission/vessel-onboarding JSON contract. */

import { mergeOnboardingModules } from './onboarding-modules.util';

export const EMISSION_SECTION_KEYS = [
  'speedVref',
  'mcrmr',
  'mcrmeLim',
  'pme',
  'pmeLim',
  'ppto',
  'fuelTypeMe',
  'cfme',
  'sfcme',
  'sfcmeLim',
  'pae',
  'fuelTypeAe',
  'cfae',
  'sfeae',
  'iceClass',
  'fi',
  'fc',
  'fj',
  'fw',
  'fm',
  'ficsr',
] as const;

const EMISSION_SNAKE_TO_CAMEL: Record<string, string> = {
  speed_vref: 'speedVref',
  mcrmr: 'mcrmr',
  mcrme_lim: 'mcrmeLim',
  pme: 'pme',
  pme_lim: 'pmeLim',
  ppto: 'ppto',
  cfme: 'cfme',
  sfcme: 'sfcme',
  sfcme_lim: 'sfcmeLim',
  pae: 'pae',
  cfae: 'cfae',
  sfeae: 'sfeae',
  fuel_type_me: 'fuelTypeMe',
  fuel_type_ae: 'fuelTypeAe',
  ice_class: 'iceClass',
  fi: 'fi',
  fc: 'fc',
  fj: 'fj',
  fw: 'fw',
  fm: 'fm',
  ficsr: 'ficsr',
};

const EMISSION_NUMERIC_KEYS = new Set<string>([
  'speedVref',
  'mcrmr',
  'mcrmeLim',
  'pme',
  'pmeLim',
  'ppto',
  'cfme',
  'sfcme',
  'sfcmeLim',
  'pae',
  'cfae',
  'sfeae',
  'fi',
  'fc',
  'fj',
  'fw',
  'fm',
  'ficsr',
]);

const EMISSION_STRING_KEYS = new Set<string>(['fuelTypeMe', 'fuelTypeAe', 'iceClass']);

export function parseEmissionNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function pickDefined<T>(...sources: (T | undefined | null)[]): T | null {
  for (const source of sources) {
    if (source !== undefined && source !== null) {
      return source;
    }
  }
  return null;
}

function pickString(...sources: unknown[]): string | null {
  for (const source of sources) {
    if (source === undefined || source === null) {
      continue;
    }
    const text = String(source).trim();
    if (text !== '') {
      return text;
    }
  }
  return null;
}

/** Extract emission section from a flat API row or nested payload. */
export function extractEmissionSection(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const nested =
    (row['emission'] as Record<string, unknown> | undefined) ??
    (row['emission_parameters'] as Record<string, unknown> | undefined);
  const section: Record<string, unknown> = nested ? { ...nested } : {};

  Object.entries(EMISSION_SNAKE_TO_CAMEL).forEach(([snake, camel]) => {
    const snakeValue = row[snake];
    const camelValue = row[camel];
    const value = pickDefined(snakeValue, camelValue, section[snake], section[camel]);
    if (value !== null) {
      section[camel] = value;
    }
  });

  return section;
}

/** Normalize a GET row into the nested POST contract shape. */
export function normalizeEmissionOnboardingApiRow(
  row: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!row) {
    return {};
  }

  const clientId = Number(row['client_id'] ?? row['client'] ?? 0);
  const vesselName = pickString(row['vessel_name'], row['vesselName'], row['ship']) ?? '';
  const imo = String(pickString(row['imo'], row['imo_number'], row['vessel_imo'], row['imoNumber']) ?? '')
    .replace(/\D/g, '');
  const vesselType = pickString(
    row['vessel_type'],
    row['vesselType'],
    row['type_of_ship'],
    row['typeOfShip'],
    row['emission_type'],
  ) ?? '';
  const segment = pickString(row['segment'], row['vessel_segment']) ?? '';

  return {
    submitted_at: row['submitted_at'] ?? row['submittedAt'],
    onboarding_modules: Array.isArray(row['onboarding_modules'])
      ? row['onboarding_modules']
      : Array.isArray(row['onboardingModules'])
        ? row['onboardingModules']
        : ['emission'],
    quick_onboarding:
      row['quick_onboarding'] === true || row['quickOnboarding'] === true,
    vessel: {
      client_id: clientId,
      vessel_name: vesselName,
      imo,
      vessel_type: vesselType,
      segment,
    },
    onboarding_form: {
      ship: vesselName,
      imoNumber: imo,
      typeOfShip: vesselType,
      segment,
      client: clientId,
      merakiPic: String(row['meraki_pic'] ?? row['merakiPic'] ?? ''),
      clientPic: String(row['client_pic'] ?? row['clientPic'] ?? row['eiger_pic'] ?? ''),
      quickOnboarding:
        row['quick_onboarding'] === true || row['quickOnboarding'] === true,
    },
    emission: extractEmissionSection(row),
  };
}

function normalizeEmissionSection(
  emission: Record<string, unknown>,
  original?: Record<string, unknown> | null,
  options?: { applyFactorDefaults?: boolean },
): Record<string, unknown> {
  const originalSection = original ?? {};
  const merged = { ...originalSection, ...emission };
  const result: Record<string, unknown> = {};
  const applyFactorDefaults = options?.applyFactorDefaults === true;
  const factorKeys = new Set(['fi', 'fc', 'fj', 'fw', 'fm', 'ficsr']);

  EMISSION_SECTION_KEYS.forEach((key) => {
    if (!(key in merged)) {
      if (applyFactorDefaults && factorKeys.has(key)) {
        result[key] = 1.0;
      }
      return;
    }

    const raw = merged[key];
    if (raw === null || raw === '') {
      result[key] = null;
      return;
    }

    if (EMISSION_NUMERIC_KEYS.has(key)) {
      if (raw === 0 || raw === false) {
        result[key] = 0;
        return;
      }
      const parsed = parseEmissionNumber(raw);
      if (parsed !== null) {
        result[key] = parsed;
      }
      return;
    }

    if (EMISSION_STRING_KEYS.has(key)) {
      const trimmed = String(raw).trim();
      result[key] = trimmed || null;
    }
  });

  return result;
}

export interface BuildEmissionOnboardingPayloadInput {
  submittedAt?: string;
  onboardingModules?: string[];
  quickOnboarding?: boolean;
  vessel?: Record<string, unknown>;
  onboardingForm?: Record<string, unknown>;
  emission?: Record<string, unknown>;
  original?: Record<string, unknown> | null;
  applyFactorDefaults?: boolean;
}

export function buildEmissionOnboardingPayload(
  input: BuildEmissionOnboardingPayloadInput,
): Record<string, unknown> {
  const original = input.original
    ? normalizeEmissionOnboardingApiRow(input.original)
    : {};

  const originalVessel = (original['vessel'] as Record<string, unknown>) ?? {};
  const originalForm = (original['onboarding_form'] as Record<string, unknown>) ?? {};
  const vesselInput = input.vessel ?? {};
  const formInput = input.onboardingForm ?? {};

  const clientId = Number(
    pickDefined(
      vesselInput['client_id'],
      formInput['client'],
      originalVessel['client_id'],
      originalForm['client'],
    ) ?? 0,
  );

  const vesselName =
    pickString(
      vesselInput['vessel_name'],
      formInput['ship'],
      originalVessel['vessel_name'],
      originalForm['ship'],
    ) ?? '';

  const imo = String(
    pickString(
      vesselInput['imo'],
      formInput['imoNumber'],
      originalVessel['imo'],
      originalForm['imoNumber'],
    ) ?? '',
  ).replace(/\D/g, '');

  const vesselType =
    pickString(
      vesselInput['vessel_type'],
      formInput['typeOfShip'],
      originalVessel['vessel_type'],
      originalForm['typeOfShip'],
    ) ?? '';

  const segment =
    pickString(
      vesselInput['segment'],
      formInput['segment'],
      originalVessel['segment'],
      originalForm['segment'],
    ) ?? '';

  const quickOnboarding =
    input.quickOnboarding !== undefined
      ? input.quickOnboarding
      : formInput['quickOnboarding'] === true ||
        original['quick_onboarding'] === true;

  const modules = mergeOnboardingModules(
    input.onboardingModules,
    original['onboarding_modules'],
    'emission',
  );

  const emissionChanges: Record<string, unknown> = {
    ...((original['emission'] as Record<string, unknown>) ?? {}),
    ...(input.emission ?? {}),
  };

  const payload: Record<string, unknown> = {
    submitted_at:
      pickString(input.submittedAt, original['submitted_at']) ??
      new Date().toISOString(),
    onboarding_modules: modules.length ? modules : mergeOnboardingModules('emission'),
    quick_onboarding: quickOnboarding,
    vessel: {
      client_id: clientId,
      vessel_name: vesselName,
      imo,
      vessel_type: vesselType,
      segment,
    },
    onboarding_form: {
      ship: pickString(formInput['ship'], vesselName) ?? vesselName,
      imoNumber: pickString(formInput['imoNumber'], imo) ?? imo,
      typeOfShip: pickString(formInput['typeOfShip'], vesselType) ?? vesselType,
      segment: pickString(formInput['segment'], segment) ?? segment,
      client: clientId,
      merakiPic: String(
        pickDefined(formInput['merakiPic'], originalForm['merakiPic']) ?? '',
      ),
      clientPic: String(
        pickDefined(
          formInput['clientPic'],
          formInput['eigerPic'],
          originalForm['clientPic'],
        ) ?? '',
      ),
      quickOnboarding,
    },
    emission: normalizeEmissionSection(
      emissionChanges,
      (original['emission'] as Record<string, unknown>) ?? {},
      { applyFactorDefaults: input.applyFactorDefaults === true },
    ),
  };

  return payload;
}

/** Build POST body from a detail/edit row, merging with the previous API snapshot. */
export function buildEmissionOnboardingPayloadFromDetail(
  detail: Record<string, unknown>,
  original?: Record<string, unknown> | null,
): Record<string, unknown> {
  const normalizedOriginal = normalizeEmissionOnboardingApiRow(original ?? detail);
  const emission = extractEmissionSection(detail);

  return buildEmissionOnboardingPayload({
    submittedAt: String(
      pickDefined(detail['submitted_at'], normalizedOriginal['submitted_at']) ??
        new Date().toISOString(),
    ),
    onboardingModules: Array.isArray(detail['onboarding_modules'])
      ? (detail['onboarding_modules'] as string[])
      : (normalizedOriginal['onboarding_modules'] as string[]),
    quickOnboarding:
      detail['quick_onboarding'] === true ||
      detail['quickOnboarding'] === true ||
      normalizedOriginal['quick_onboarding'] === true,
    vessel: {
      client_id: Number(detail['client_id'] ?? 0),
      vessel_name: pickString(detail['vessel_name'], detail['vesselName']) ?? '',
      imo: String(pickString(detail['imo'], detail['vessel_imo']) ?? '').replace(/\D/g, ''),
      vessel_type: pickString(
        detail['vessel_type'],
        detail['emission_type'],
        detail['type_of_ship'],
      ) ?? '',
      segment: pickString(detail['segment'], detail['vessel_segment']) ?? '',
    },
    onboardingForm: {
      ship: pickString(detail['vessel_name'], detail['vesselName']) ?? '',
      imoNumber: String(pickString(detail['imo'], detail['vessel_imo']) ?? '').replace(/\D/g, ''),
      typeOfShip: pickString(detail['vessel_type'], detail['type_of_ship']) ?? '',
      segment: pickString(detail['segment']) ?? '',
      client: Number(detail['client_id'] ?? 0),
      merakiPic: String(detail['meraki_pic'] ?? detail['merakiPic'] ?? ''),
      clientPic: String(detail['client_pic'] ?? detail['clientPic'] ?? detail['eiger_pic'] ?? ''),
      quickOnboarding:
        detail['quick_onboarding'] === true || detail['quickOnboarding'] === true,
    },
    emission,
    original: normalizedOriginal,
  });
}
